import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocationsCache } from '../hooks/useLocationsCache';
import { useGeneralContext } from './GeneralContext';
import { useSensorReadingsContext } from './SensorReadingsContext';
import { checkStatus } from '../utils/checkStatus';
import updateSensorsAndAssetStatus from '../utils/updateSensorsAndAssetStatus';
import { useFilter } from './FilterContext';

const LocationsCacheContext = createContext({ state: {}, actions: {} });

export const initLocFilterData = {
  filter: [],
  filterIds: new Set(),
  userGroup: new Set(),
  status: [],
};

const LocationsCacheProvider = ({ children }) => {
  const { user, isMapPage } = useGeneralContext();
  const { i18n } = useTranslation();
  const { readings } = useSensorReadingsContext();
  const [disabledRequest, setDisabledRequest] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const { getFilterData, setFilterData } = useFilter();
  const filterData = useMemo(
    () => getFilterData('locations') ?? {},
    [getFilterData],
  );
  const setFilterDataLocations = useCallback(
    data => setFilterData('locations', data),
    [setFilterData],
  );

  const { refetch, isLoading, data, isFetching, dataUpdatedAt } =
    useLocationsCache(!disabledRequest && !!user, refresh || firstLoad);

  useEffect(() => {
    if (!filterData.filter) {
      setFilterDataLocations(initLocFilterData);
    }
  }, [filterData.filter, setFilterDataLocations]);

  useEffect(() => {
    if (firstLoad) setFirstLoad(false);
  }, [firstLoad]);

  useEffect(() => {
    const refreshMap = async () => {
      if (refresh) {
        await refetch();
        setRefresh(false);
      }
    };
    refreshMap();
  }, [refetch, refresh]);

  useEffect(() => {
    if (isMapPage && data && window.map?.updateMap) {
      window.map.updateMap();
    }
  }, [data, isMapPage]);

  const collator = useMemo(
    () =>
      new Intl.Collator(i18n.language, {
        numeric: true,
        sensitivity: 'base',
      }),
    [i18n.language],
  );

  const assets = useMemo(
    () =>
      (data?.features || [])
        .filter(
          feature =>
            feature.geometry.type === 'Point' &&
            feature.properties.type !== 'BLD',
        )
        .sort((a, b) => collator.compare(a.properties.name, b.properties.name))
        .map(a => {
          const [, , assetPosition] = updateSensorsAndAssetStatus(
            a.properties,
            readings,
          );
          if (assetPosition) {
            a.geometry.coordinates = [assetPosition.lng, assetPosition.lat];
          }
          return { ...a };
        }),
    [collator, data?.features, readings],
  );

  const buildings = useMemo(
    () =>
      (data?.features || [])
        .filter(feature => feature.properties.type === 'BLD')
        .sort((a, b) => collator.compare(a.properties.name, b.properties.name))
        .map(bld => {
          let worstStatusBuilding = 'OFF';
          let worstMessageBuilding = 'OFF';
          bld.properties.assets_status = {
            NORMAL: 0,
            WARNING: 0,
            DANGER: 0,
            ON: 0,
            OFF: 0,
          };
          bld.properties.floors = bld.properties.floors.map(floor => {
            let worstStatusFloor = 'OFF';
            let worstMessageFloor = 'OFF';
            floor.assets_status = {
              NORMAL: 0,
              WARNING: 0,
              DANGER: 0,
              ON: 0,
              OFF: 0,
            };
            floor.zones = floor.zones.map(zone => {
              let worstStatusZone = 'OFF';
              let worstMessageZone = 'OFF';
              zone.assets_status = {
                NORMAL: 0,
                WARNING: 0,
                DANGER: 0,
                ON: 0,
                OFF: 0,
              };
              zone.assets = zone.assets.map(a => {
                const [worstStatusAsset, worstMessageAsset] =
                  updateSensorsAndAssetStatus(a, readings);
                zone.assets_status[worstStatusAsset]++;
                floor.assets_status[worstStatusAsset]++;
                bld.properties.assets_status[worstStatusAsset]++;
                [worstStatusZone, worstMessageZone] = checkStatus(
                  worstStatusZone,
                  worstMessageZone,
                  worstStatusAsset,
                  worstMessageAsset,
                );
                return { ...a };
              });
              zone.status = worstStatusZone;
              zone.message = worstMessageZone;
              [worstStatusFloor, worstMessageFloor] = checkStatus(
                worstStatusFloor,
                worstMessageFloor,
                worstStatusZone,
                worstMessageZone,
              );
              return { ...zone };
            });
            floor.status = worstStatusFloor;
            floor.message = worstMessageFloor;
            [worstStatusBuilding, worstMessageBuilding] = checkStatus(
              worstStatusBuilding,
              worstMessageBuilding,
              worstStatusFloor,
              worstMessageFloor,
            );
            return { ...floor };
          });
          bld.properties.status = worstStatusBuilding;
          bld.properties.message = worstMessageBuilding;
          return { ...bld };
        }),
    [collator, data?.features, readings],
  );

  const sensors = useMemo(
    () =>
      assets
        .flatMap(item => item.properties.sensors)
        .concat(
          buildings
            .flatMap(item => item.properties.floors)
            .flatMap(item => item.zones)
            .flatMap(item => item.assets)
            .flatMap(item => item.sensors),
        )
        .sort((a, b) => collator.compare(a.name, b.name)),
    [assets, buildings, collator],
  );

  const actuators = useMemo(
    () =>
      assets
        .flatMap(item => item.properties.actuators)
        .concat(
          buildings
            .flatMap(item => item.properties.floors)
            .flatMap(item => item.zones)
            .flatMap(item => item.assets)
            .flatMap(item => item.actuators),
        )
        .sort((a, b) => collator.compare(a.name, b.name)),
    [assets, buildings, collator],
  );

  const locations = useMemo(
    () =>
      (data?.features || [])
        .filter(feature => feature.properties.type === 'LOC')
        .sort((a, b) => collator.compare(a.properties.name, b.properties.name))
        .map(loc => {
          let worstStatus = 'OFF';
          let worstMessage = 'OFF';
          assets
            .filter(
              a =>
                a.properties.is_placed &&
                a.properties.location === loc.properties.id,
            )
            .forEach(a => {
              const nextStatus = a.properties.status;
              const nextMessage = a.properties.message;
              [worstStatus, worstMessage] = checkStatus(
                worstStatus,
                worstMessage,
                nextStatus,
                nextMessage,
              );
            });
          buildings
            .filter(bld => bld.properties.parent === loc.properties.id)
            .forEach(bld => {
              const nextStatus = bld.properties.status;
              const nextMessage = bld.properties.message;
              [worstStatus, worstMessage] = checkStatus(
                worstStatus,
                worstMessage,
                nextStatus,
                nextMessage,
              );
            });
          loc.properties.status = worstStatus;
          loc.properties.message = worstMessage;
          return { ...loc };
        }),
    [assets, buildings, collator, data?.features],
  );

  const lastUpdated = useMemo(() => new Date(data?.date), [data]);

  const refreshMap = useCallback(async () => setRefresh(true), []);

  const isFilterApplied = useMemo(
    () =>
      filterData.userGroup?.size > 0 ||
      filterData.filterIds?.size > 0 ||
      filterData.status?.length > 0,
    [
      filterData.userGroup?.size,
      filterData.filterIds?.size,
      filterData.status?.length,
    ],
  );

  const hasUserGroupFilter = useCallback(
    record =>
      (record.user_group ?? []).some(group => filterData.userGroup?.has(group)),
    [filterData.userGroup],
  );

  const hasIdFilter = useCallback(
    id => filterData.filterIds?.has(id),
    [filterData.filterIds],
  );

  const hasStatusFilter = useCallback(
    status => filterData.status?.includes(status),
    [filterData.status],
  );

  const filteredLocations = useMemo(
    () =>
      locations.filter(location => {
        const locationBuildings = buildings.filter(
          a => a.properties.parent === location.properties.id,
        );
        const locationAssets = assets.filter(
          a =>
            a.properties.location === location.properties.id &&
            a.properties.is_placed,
        );
        return (
          !isFilterApplied ||
          hasUserGroupFilter(location.properties) ||
          hasStatusFilter(location.properties.status) ||
          hasIdFilter(location.properties.id) ||
          locationBuildings.some(building =>
            hasIdFilter(building.properties.id),
          ) ||
          locationAssets.some(asset => hasIdFilter(asset.properties.id))
        );
      }),
    [
      assets,
      buildings,
      hasIdFilter,
      hasStatusFilter,
      hasUserGroupFilter,
      isFilterApplied,
      locations,
    ],
  );

  const filterLogic = useCallback(
    a =>
      !isFilterApplied ||
      hasIdFilter(a.properties.id) ||
      hasIdFilter(a.properties.parent) ||
      hasIdFilter(a.properties.location) ||
      hasStatusFilter(a.properties.status) ||
      hasUserGroupFilter(a.properties),
    [hasIdFilter, hasStatusFilter, hasUserGroupFilter, isFilterApplied],
  );

  const filteredBuildings = useMemo(
    () => buildings.filter(a => filterLogic(a)),
    [buildings, filterLogic],
  );
  const filteredAssets = useMemo(
    () => assets.filter(a => a.properties.is_placed && filterLogic(a)),
    [assets, filterLogic],
  );

  const values = useMemo(
    () => ({
      refetch,
      refreshMap,
      refresh,
      isLoading,
      locations,
      assets,
      buildings,
      sensors,
      actuators,
      lastUpdated,
      setDisabledRequest,
      shouldCollapse,
      setShouldCollapse,
      isFetching,
      dataUpdatedAt,
      filterData,
      setFilterData: setFilterDataLocations,
      filteredLocations,
      filteredBuildings,
      filteredAssets,
      isFilterApplied,
    }),
    [
      refetch,
      refreshMap,
      refresh,
      isLoading,
      locations,
      assets,
      buildings,
      sensors,
      actuators,
      lastUpdated,
      shouldCollapse,
      isFetching,
      dataUpdatedAt,
      filterData,
      setFilterDataLocations,
      filteredLocations,
      filteredBuildings,
      filteredAssets,
      isFilterApplied,
    ],
  );

  return (
    <LocationsCacheContext.Provider value={values}>
      {children}
    </LocationsCacheContext.Provider>
  );
};

export const useLocationsCacheContext = () => {
  return useContext(LocationsCacheContext);
};

LocationsCacheProvider.defaultProps = {
  children: null,
};

LocationsCacheProvider.propTypes = {
  children: PropTypes.element,
};

export default LocationsCacheProvider;
